import React from 'react';
import { Link } from 'react-router-dom';
import { Car, CheckCircle2, XCircle } from 'lucide-react';
import { useT } from '../services/i18n';
import VehicleBrandLogo from './VehicleBrandLogo';
import GarageSwitcher from './GarageSwitcher';
import { vehicleEnergy, energyLabel, fuelTypesLabel } from '../services/fuel';

// Green / red banner at the top of a product page telling whether the part fits the selected vehicle
const VehicleFitBadge = ({ vehicle, fits, universal, partsLink, garage = false, fuelKo = false, fuelTypes = [] }) => {
  const { t } = useT();
  const vEnergy = vehicleEnergy(vehicle?.engine?.fuel);
  const switcher = garage ? <GarageSwitcher vehicle={vehicle} testId="product-vehicle" className="mb-3 -mt-1" /> : null;
  if (!vehicle) {
    return (<>
      <div data-testid="product-vehicle-fit-none" className="flex flex-wrap items-center gap-2 text-sm px-3 py-2 rounded-lg border bg-gray-50 border-gray-200 text-gray-600 mb-3">
        <Car size={18} className="text-gray-400" />
        <span>{t('product.fitSelectHint')}</span>
        <Link to="/#selecteur-vehicule" data-testid="product-vehicle-fit-select" className="ml-auto text-xs font-semibold text-[#1e3a5f] hover:underline">{t('product.fitSelectCta')} →</Link>
      </div>
      {switcher}
    </>);
  }
  const label = `${vehicle.brand} ${vehicle.model}${vehicle.engine?.name ? ` (${vehicle.engine.name})` : ''}`;
  if (fits) {
    return (<>
      <div data-testid="product-vehicle-fit-ok" className="flex flex-wrap items-center gap-2 text-sm px-3 py-2 rounded-lg border bg-green-50 border-green-300 text-green-800 mb-3">
        <CheckCircle2 size={18} className="text-green-600" />
        <span><strong>{universal ? t('product.fitUniversal') : t('product.fitOk')}</strong> · <VehicleBrandLogo brand={vehicle.brand} bare size={18} className="align-middle mr-1" testId="product-fit-logo" />{label}</span>
        <VehicleBrandLogo brand={vehicle.brand} model={vehicle.model} size={36} preferModel zoom className="ml-1" testId="product-fit-vehicle-photo" />
        <Link to="/#selecteur-vehicule" data-testid="product-vehicle-fit-change" className="ml-auto text-xs text-green-700 hover:underline">{t('product.fitChangeVehicle')}</Link>
      </div>
      {switcher}
    </>);
  }
  return (<>
    <div data-testid="product-vehicle-fit-ko" className="flex flex-wrap items-center gap-2 text-sm px-3 py-2 rounded-lg border bg-red-50 border-red-300 text-red-800 mb-3">
      <XCircle size={18} className="text-red-600" />
      <span><strong>{fuelKo ? t('product.fitFuelKo', { e: fuelTypesLabel(t, fuelTypes), ve: vEnergy ? energyLabel(t, vEnergy) : '' }) : t('product.fitKo')}</strong> · <VehicleBrandLogo brand={vehicle.brand} bare size={18} className="align-middle mr-1" testId="product-fit-logo" />{label}</span>
      <VehicleBrandLogo brand={vehicle.brand} model={vehicle.model} size={36} preferModel zoom className="ml-1" testId="product-fit-vehicle-photo" />
      <span className="ml-auto flex items-center gap-3">
        <Link to={partsLink} data-testid="product-vehicle-fit-parts" className="text-xs font-semibold text-red-700 hover:underline">{t('product.fitSeeCompatible')} →</Link>
        <Link to="/#selecteur-vehicule" data-testid="product-vehicle-fit-change" className="text-xs text-red-700/80 hover:underline">{t('product.fitChangeVehicle')}</Link>
      </span>
    </div>
    {switcher}
  </>);
};

export default VehicleFitBadge;
